import React, { useState, useMemo, useEffect } from 'react';
import { ExternalLink, Calendar, Clock, Users, ChevronLeft, ChevronRight } from 'lucide-react';

// Helper : nom du pro (support portail `employee_name` + vitrine `coach_name`)
const proName = (c) => c.employee_name || c.coach_name || '';

// Helper : prix effectif à payer par le client courant
// (portail: `client_price` calculé selon is_member_pricing, sinon fallback member_price/price)
// (vitrine: `price` seul est présent)
const effectivePrice = (c) => c.client_price ?? c.member_price ?? c.price ?? 0;

// Helper : prix d'origine (avant yield) — priorise `client_original_price` (aligné popup)
const originalPrice = (c) => c.client_original_price ?? c.original_member_price ?? c.original_price ?? null;

// ─────────────────────────────────────────────────────────────
// Vue Liste — même design que la vitrine (rows date | pro | prix)
// ─────────────────────────────────────────────────────────────
function CoursListView({ courses, lang, onBook, inscrireLabel }) {
  const locales = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE' };
  const locale = locales[lang] || 'fr-FR';

  return (
    <div className="bg-white rounded-lg border border-[#D1C9C0]/40 divide-y divide-[#D1C9C0]/30 px-3 sm:px-6">
      {courses.map(course => {
        const dt = new Date(course.datetime);
        const dayName = dt.toLocaleDateString(locale, { weekday: 'short' });
        const dayNum = dt.getDate();
        const timeStr = dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
        const durH = Math.floor((course.duration_minutes || 60) / 60);
        const durM = (course.duration_minutes || 60) % 60;
        const durStr = durM ? `${durH}h${durM}` : `${durH}h`;
        const pName = proName(course);
        const price = effectivePrice(course);
        const origPrice = originalPrice(course);
        const showYield = course.yield_label && origPrice && origPrice !== price;

        return (
          <div key={course.id} className="flex items-center gap-3 sm:gap-6 py-4 group" data-testid={`cours-row-${course.id}`}>
            <div className="shrink-0 w-12 sm:w-14 text-center">
              <span className="block text-[10px] uppercase tracking-wider text-[#4A4A4A]/60">{dayName}</span>
              <span className="block text-xl sm:text-2xl font-['Playfair_Display'] text-[#1F3D2B] font-light leading-none">{dayNum}</span>
              <span className="block text-[10px] uppercase tracking-wider text-[#4A4A4A]/60 mt-0.5">{dt.toLocaleDateString(locale, { month: 'short' }).replace('.', '')}</span>
            </div>
            <div className="w-px h-10 bg-[#D1C9C0]/50 shrink-0 hidden sm:block" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h4 className="font-medium text-[#0B0B0B] text-sm sm:text-base leading-tight">{course.course_name}</h4>
                {course.yield_label && (
                  <span
                    className="text-[8px] sm:text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm whitespace-nowrap"
                    style={{ color: '#B8860B', backgroundColor: 'rgba(212, 175, 55, 0.12)', border: '1px solid rgba(212, 175, 55, 0.35)' }}
                    data-testid={`yield-badge-${course.id}`}
                  >
                    {course.yield_label}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[#4A4A4A]/70">
                <span className="flex items-center gap-1"><Clock size={11} /> {timeStr}</span>
                <span>{durStr}</span>
                {pName && <span className="flex items-center gap-1"><Users size={11} /> {pName}</span>}
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1.5">
              <div className="flex items-baseline gap-1">
                {showYield && <span className="text-[10px] text-[#4A4A4A]/50 line-through">{origPrice}&euro;</span>}
                {price > 0 && <span className="text-sm font-semibold text-[#1F3D2B]">{price}&euro;</span>}
              </div>
              <button
                onClick={() => onBook(course)}
                className="inline-flex items-center gap-1 px-2.5 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-[11px] uppercase tracking-[0.1em] bg-white border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white transition-all duration-300 rounded-sm whitespace-nowrap"
                data-testid={`cours-book-${course.id}`}
              >
                {inscrireLabel} <ExternalLink size={10} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Vue Calendrier mensuelle — même design que la vitrine
// ─────────────────────────────────────────────────────────────
function CoursCalendarView({ courses, lang, onBook, inscrireLabel }) {
  const [monthDate, setMonthDate] = useState(() => {
    const first = courses.find(c => c.datetime);
    return first ? new Date(first.datetime) : new Date();
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const locales = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE' };
  const locale = locales[lang] || 'fr-FR';

  const grid = useMemo(() => {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth();
    const first = new Date(y, m, 1);
    const shift = (first.getDay() + 6) % 7;
    const gridStart = new Date(y, m, 1 - shift);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const totalCells = Math.ceil((shift + daysInMonth) / 7) * 7;
    return Array.from({ length: totalCells }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [monthDate]);

  const coursesByDay = useMemo(() => {
    const map = {};
    courses.forEach(c => {
      if (!c.datetime) return;
      const d = new Date(c.datetime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.datetime) - new Date(b.datetime)));
    return map;
  }, [courses]);

  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const monthLabel = monthDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const capitalizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
  const weekdayLabels = lang === 'de'
    ? ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
    : lang === 'en'
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const currentMonth = monthDate.getMonth();
  const today = new Date();
  const todayKey = dayKey(today);

  const goPrevMonth = () => { setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1)); setSelectedDay(null); };
  const goNextMonth = () => { setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1)); setSelectedDay(null); };

  const selectedCourses = selectedDay ? (coursesByDay[selectedDay] || []) : [];
  const emptyLabel = lang === 'fr' ? 'Aucun cours ce jour' : lang === 'en' ? 'No lessons that day' : 'Kein Kurs an diesem Tag';
  const clickHintLabel = lang === 'fr' ? 'Cliquez sur un jour pour voir les cours' : lang === 'en' ? 'Click a day to view lessons' : 'Klicken Sie auf einen Tag';

  return (
    <div className="bg-white rounded-lg border border-[#D1C9C0]/40 p-4 sm:p-6" data-testid="cours-calendar-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={goPrevMonth} className="p-2 hover:bg-[#F5F5F3] rounded-sm transition-colors" data-testid="cal-prev-month" aria-label="Mois précédent">
          <ChevronLeft size={16} strokeWidth={1.5} className="text-[#1F3D2B]" />
        </button>
        <h4 className="font-['Playfair_Display'] text-lg sm:text-xl text-[#0B0B0B] font-light" data-testid="cal-month-label">{capitalizedMonth}</h4>
        <button onClick={goNextMonth} className="p-2 hover:bg-[#F5F5F3] rounded-sm transition-colors" data-testid="cal-next-month" aria-label="Mois suivant">
          <ChevronRight size={16} strokeWidth={1.5} className="text-[#1F3D2B]" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdayLabels.map(w => (
          <div key={w} className="text-center text-[10px] uppercase tracking-[0.15em] text-[#4A4A4A]/70 py-1">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, i) => {
          const key = dayKey(d);
          const isOtherMonth = d.getMonth() !== currentMonth;
          const isSelected = selectedDay === key;
          const isTodayCell = key === todayKey;
          const dayCourses = coursesByDay[key] || [];
          const count = dayCourses.length;
          const hasYield = dayCourses.some(c => c.yield_label);
          const isClickable = count > 0;

          return (
            <button
              key={i}
              onClick={() => isClickable && setSelectedDay(isSelected ? null : key)}
              disabled={!isClickable}
              data-testid={`cal-day-${key}`}
              className={`relative aspect-square flex flex-col items-center justify-center gap-0.5 rounded-sm transition-all ${
                isSelected
                  ? 'bg-[#1F3D2B] text-white shadow-md'
                  : isOtherMonth
                    ? 'text-[#4A4A4A]/25'
                    : isClickable
                      ? 'text-[#0B0B0B] hover:bg-[#F5F5F3] cursor-pointer'
                      : 'text-[#4A4A4A]/45 cursor-default'
              } ${isTodayCell && !isSelected ? 'ring-1 ring-[#D4AF37]/60' : ''}`}
            >
              <div className={`font-['Playfair_Display'] leading-none ${count > 0 ? 'text-base sm:text-lg' : 'text-sm sm:text-base font-light'}`}>
                {d.getDate()}
              </div>
              {count > 0 && (
                <span
                  className={`text-[8px] font-semibold leading-none px-1.5 py-0.5 rounded-full ${
                    isSelected
                      ? 'bg-white/25 text-white'
                      : hasYield
                        ? 'bg-[#D4AF37]/15 text-[#8B6914]'
                        : 'bg-[#1F3D2B]/10 text-[#1F3D2B]'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay ? (
        <div className="mt-6 pt-6 border-t border-[#D1C9C0]/40" data-testid="cal-selected-day-panel">
          <p className="text-[11px] uppercase tracking-[0.15em] text-[#4A4A4A]/70 mb-3">
            {new Date(selectedDay).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          {selectedCourses.length === 0 ? (
            <p className="text-sm text-[#4A4A4A]/70 italic">{emptyLabel}</p>
          ) : (
            <div className="space-y-2">
              {selectedCourses.map(course => {
                const dt = new Date(course.datetime);
                const timeStr = dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
                const durH = Math.floor((course.duration_minutes || 60) / 60);
                const durM = (course.duration_minutes || 60) % 60;
                const durStr = durM ? `${durH}h${durM}` : `${durH}h`;
                const pName = proName(course);
                const price = effectivePrice(course);
                const origPrice = originalPrice(course);
                const showYield = course.yield_label && origPrice && origPrice !== price;

                return (
                  <div key={course.id} className="flex items-center gap-2 sm:gap-4 py-3 border-b border-[#D1C9C0]/30 last:border-0" data-testid={`cal-course-row-${course.id}`}>
                    <div className="w-12 sm:w-16 shrink-0 text-[#1F3D2B] font-medium text-sm">{timeStr}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h5 className="font-medium text-[#0B0B0B] text-sm leading-tight">{course.course_name}</h5>
                        {course.yield_label && (
                          <span
                            className="text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm whitespace-nowrap"
                            style={{ color: '#B8860B', backgroundColor: 'rgba(212, 175, 55, 0.12)', border: '1px solid rgba(212, 175, 55, 0.35)' }}
                            data-testid={`cal-yield-badge-${course.id}`}
                          >
                            {course.yield_label}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-[#4A4A4A]/70">
                        <span>{durStr}</span>
                        {pName && <span className="flex items-center gap-1"><Users size={10} /> {pName}</span>}
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      <div className="flex items-baseline gap-1">
                        {showYield && <span className="text-[10px] text-[#4A4A4A]/50 line-through">{origPrice}&euro;</span>}
                        {price > 0 && <span className="text-sm font-semibold text-[#1F3D2B]">{price}&euro;</span>}
                      </div>
                      <button
                        onClick={() => onBook(course)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.1em] bg-white border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white transition-all duration-300 rounded-sm whitespace-nowrap"
                        data-testid={`cal-cours-book-${course.id}`}
                      >
                        {inscrireLabel} <ExternalLink size={10} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <p className="text-center text-[11px] text-[#4A4A4A]/50 italic mt-6 pt-4 border-t border-[#D1C9C0]/40">{clickHintLabel}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Wrapper avec toggle Liste / Calendrier
// ─────────────────────────────────────────────────────────────
export default function CoursListCalendar({
  courses,
  loading = false,
  lang = 'fr',
  onBook,
  showHeader = true,
  storageKey = 'cours-view',
}) {
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(storageKey) || 'calendar'; } catch { return 'calendar'; }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, view); } catch { /* ignore */ }
  }, [view, storageKey]);

  const t = {
    fr: { title: 'Cours disponibles', list: 'Liste', calendar: 'Calendrier', empty: 'Aucun cours disponible pour le moment.', inscrire: "S'inscrire" },
    en: { title: 'Available lessons', list: 'List', calendar: 'Calendar', empty: 'No lessons available at this time.', inscrire: 'Register' },
    de: { title: 'Verfügbare Kurse', list: 'Liste', calendar: 'Kalender', empty: 'Keine Kurse verfügbar.', inscrire: 'Anmelden' },
  }[lang] || {};

  return (
    <div data-testid="cours-disponibles">
      {showHeader && (
        <div className="flex items-center gap-3 mb-6">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-[#1F3D2B] flex items-center gap-3">
            <Calendar size={16} />
            {t.title}
          </h3>
          <div className="flex-1 h-px bg-[#D1C9C0]/40" />
          <div className="inline-flex bg-white border border-[#D1C9C0]/50 rounded-sm overflow-hidden" data-testid="cours-view-toggle">
            <button
              onClick={() => setView('calendar')}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] transition-colors ${view === 'calendar' ? 'bg-[#1F3D2B] text-white' : 'text-[#4A4A4A] hover:text-[#1F3D2B]'}`}
              data-testid="cours-view-calendar"
            >
              {t.calendar}
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] transition-colors ${view === 'list' ? 'bg-[#1F3D2B] text-white' : 'text-[#4A4A4A] hover:text-[#1F3D2B]'}`}
              data-testid="cours-view-list"
            >
              {t.list}
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-16 bg-white/60 rounded animate-pulse" />)}</div>
      ) : courses.length === 0 ? (
        <p className="text-[#4A4A4A] text-sm italic py-6">{t.empty}</p>
      ) : view === 'calendar' ? (
        <CoursCalendarView courses={courses} lang={lang} onBook={onBook} inscrireLabel={t.inscrire} />
      ) : (
        <CoursListView courses={courses} lang={lang} onBook={onBook} inscrireLabel={t.inscrire} />
      )}
    </div>
  );
}
